import * as XLSX from 'xlsx';
import {
  blankAoaOutsideMergeOrigins,
  buildExcelMergeRanges,
  columnsHaveCellMerge,
} from 'components/grid/hiGrid/core/bodyMergeUtils';

/**
 * 그리드 핸들에서 내보내기용 컬럼 정의 추출
 * @returns {{ gridId, gridLabel, field, header, required, key, mergeRows, mergeGroupKey }[]}
 */
export function getExportColumnDefs(handle) {
  if (!handle) return [];
  const cols = typeof handle.getDataColumns === 'function' ? handle.getDataColumns() : [];
  return (cols || [])
    .filter((c) => c && c.visible !== false && c.meta?.visible !== false)
    .map((c) => {
      const field = c.accessorKey || c.id || '';
      return {
        gridId: handle.id,
        gridLabel: handle.label || handle.id,
        field,
        header: String(c.header ?? field).replace(/^\*\s*/, ''),
        required: c.meta?.required === true,
        key: `${handle.id}::${field}`,
        mergeRows: c.meta?.mergeRows === true,
        mergeGroupKey: c.meta?.mergeGroupKey || null,
      };
    })
    .filter((c) => c.field);
}

/** 핸들 그리드에 본문 셀 병합 컬럼이 있는지 */
export function handleHasCellMergeColumns(handle) {
  const cols = typeof handle?.getDataColumns === 'function' ? handle.getDataColumns() : [];
  return columnsHaveCellMerge(cols);
}

function resolveContext(handle) {
  const ctx = handle?.contextValues;
  if (typeof ctx === 'function') return ctx() || {};
  return ctx && typeof ctx === 'object' ? ctx : {};
}

/**
 * 행에 contextValues를 덮어쓰지 않고 기본값으로 병합한 뒤, 컬럼 key로 펼침
 * joinKeys는 접두사 없이 유지 (조인용)
 */
export function rowsToKeyed(handle) {
  const cols = getExportColumnDefs(handle);
  const joinKeys = Array.isArray(handle.joinKeys) ? handle.joinKeys : [];
  const raw = typeof handle.getData === 'function' ? handle.getData() : [];
  const context = resolveContext(handle);

  return (raw || []).map((row) => {
    const src = { ...context, ...(row || {}) };
    const out = {};
    for (const jk of joinKeys) {
      if (src[jk] != null && src[jk] !== '') out[jk] = src[jk];
    }
    for (const c of cols) {
      out[c.key] = src[c.field] != null && src[c.field] !== '' ? src[c.field] : '';
    }
    return out;
  });
}

function commonJoinKeys(leftHandle, rightHandle) {
  const a = new Set(Array.isArray(leftHandle.joinKeys) ? leftHandle.joinKeys : []);
  const b = Array.isArray(rightHandle.joinKeys) ? rightHandle.joinKeys : [];
  return b.filter((k) => a.has(k));
}

/**
 * 공통 joinKeys 기준 left join. 공통 키가 없으면
 * - left 1행: right와 1:N 결합
 * - left N행: right 컬럼을 비운 채 left만 유지
 */
export function leftJoinKeyed(leftRows, rightRows, joinKeys) {
  if (!leftRows?.length) return [];
  if (!rightRows?.length) return leftRows.map((r) => ({ ...r }));

  if (!joinKeys.length) {
    if (leftRows.length === 1) {
      return rightRows.map((r) => ({ ...leftRows[0], ...r }));
    }
    return leftRows.map((r) => ({ ...r }));
  }

  const index = new Map();
  for (const r of rightRows) {
    const k = joinKeys.map((jk) => String(r[jk] ?? '')).join('\u0001');
    if (!index.has(k)) index.set(k, []);
    index.get(k).push(r);
  }

  const out = [];
  for (const l of leftRows) {
    const k = joinKeys.map((jk) => String(l[jk] ?? '')).join('\u0001');
    const matches = index.get(k);
    if (!matches?.length) {
      out.push({ ...l });
    } else {
      for (const m of matches) {
        out.push({ ...l, ...m });
      }
    }
  }
  return out;
}

/**
 * 선택한 그리드들을 order 순으로 left-join 병합
 * @param {object[]} handles - 선택된 그리드 핸들 (등록 order 순으로 정렬 권장)
 * @param {string[]} selectedColKeys - `${gridId}::${field}` 목록
 */
export function buildMergedAoa(handles, selectedColKeys) {
  if (!handles?.length || !selectedColKeys?.length) {
    return { aoa: [], rowCount: 0, exportFields: [], selectedCols: [] };
  }

  const colDefMap = new Map();
  for (const h of handles) {
    for (const c of getExportColumnDefs(h)) {
      colDefMap.set(c.key, c);
    }
  }
  const selectedCols = selectedColKeys
    .map((k) => colDefMap.get(k))
    .filter(Boolean);

  let rows = rowsToKeyed(handles[0]);
  for (let i = 1; i < handles.length; i += 1) {
    const right = rowsToKeyed(handles[i]);
    const preferred = commonJoinKeys(handles[0], handles[i]);
    const fallback = commonJoinKeys(handles[i - 1], handles[i]);
    rows = leftJoinKeyed(rows, right, preferred.length ? preferred : fallback);
  }

  const headers = selectedCols.map((c) =>
    handles.length > 1 ? `${c.gridLabel}.${c.header}` : c.header
  );
  const aoa = [headers];
  for (const row of rows) {
    aoa.push(selectedCols.map((c) => (row[c.key] != null && row[c.key] !== '' ? row[c.key] : '')));
  }
  return {
    aoa,
    rowCount: rows.length,
    exportFields: selectedCols.map((c) => c.field),
    selectedCols,
  };
}

/** 단일 그리드 AOA */
export function buildSingleAoa(handle, selectedColKeys) {
  return buildMergedAoa([handle], selectedColKeys);
}

/**
 * 단일 그리드 — 셀 병합 / 병합 해제 시트 구성
 * @param {{ includeMerged?: boolean, includeUnmerged?: boolean }} opts
 * @returns {{ sheets: Array<{ name: string, aoa: any[][], merges?: any[] }>, rowCount: number }}
 */
export function buildSingleCellMergeSheets(handle, selectedColKeys, opts = {}) {
  const includeMerged = opts.includeMerged !== false;
  const includeUnmerged = opts.includeUnmerged !== false;
  const base = buildSingleAoa(handle, selectedColKeys);
  if (!base.rowCount) return { sheets: [], rowCount: 0 };

  const raw = typeof handle.getData === 'function' ? handle.getData() || [] : [];
  const dataColumns = typeof handle.getDataColumns === 'function' ? handle.getDataColumns() || [] : [];
  const hasMerge = columnsHaveCellMerge(dataColumns);
  const sheets = [];

  if (includeUnmerged) {
    sheets.push({
      name: hasMerge ? '병합해제' : 'Sheet1',
      aoa: base.aoa,
    });
  }

  if (includeMerged && hasMerge) {
    const merges = buildExcelMergeRanges(raw, dataColumns, base.exportFields);
    const aoa = merges.length
      ? blankAoaOutsideMergeOrigins(base.aoa.map((row) => row.slice()), merges)
      : base.aoa.map((row) => row.slice());
    sheets.push({
      name: '셀병합',
      aoa,
      merges,
    });
  }

  if (!sheets.length) {
    sheets.push({ name: 'Sheet1', aoa: base.aoa });
  }

  return { sheets, rowCount: base.rowCount };
}

export function writeAoaToXlsx(aoa, fileName = 'grid_export') {
  writeSheetsToXlsx([{ name: 'Sheet1', aoa }], fileName);
}

/**
 * 여러 시트를 한 엑셀 파일로 저장
 * @param {Array<{ name: string, aoa: any[][], merges?: any[] }>} sheets
 */
export function writeSheetsToXlsx(sheets, fileName = 'grid_export') {
  const list = (sheets || []).filter((s) => s?.aoa?.length);
  if (!list.length) {
    throw new Error('다운로드할 시트가 없습니다.');
  }
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  list.forEach((sheet, idx) => {
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    if (sheet.merges?.length) {
      ws['!merges'] = sheet.merges;
    }
    let name = String(sheet.name || `Sheet${idx + 1}`).slice(0, 31);
    if (usedNames.has(name)) {
      name = `${name.slice(0, 28)}_${idx + 1}`;
    }
    usedNames.add(name);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  const name = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  XLSX.writeFile(wb, name);
}
